import { Trashcan } from '@fountain/fountain-ui-components';
import {
  IconButton,
  Switch,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { MergeKey, MergeKeysService } from 'api-clients/monolith';
import React, { VFC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';

import { messages } from './messages';

// ********************************************************************************
// == Type ========================================================================
type Props = {
  accountExternalId: string;
  mergeKey: MergeKey;
  onDeleteRequest: (row: MergeKey) => void;
  onUpdated: () => void;
};

// == Constant ====================================================================
const patchService = (
  accountId: string,
  id: string,
  requestBody?: { key?: string; translatable?: boolean },
) => MergeKeysService.patchInternalApiMergeKeys(accountId, id, requestBody);

const useStyles = makeStyles({
  actionsCell: {
    width: 80,
  },
  toggleCell: {
    width: 120,
  },
});

// == Component ===================================================================
const MergeKeyRow: VFC<Props> = ({
  accountExternalId,
  mergeKey,
  onDeleteRequest,
  onUpdated,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();

  // -- Mutation ------------------------------------------------------------------
  const { mutation: patchMergeKey, result } = useApiServiceMutation<
    // eslint-disable-next-line camelcase
    { merge_key: MergeKey },
    typeof patchService
  >(patchService, {
    onSuccess: () => {
      dispatch(
        addMessageAction(intl.formatMessage(messages.updateSuccess), 'success'),
      );
      onUpdated();
    },
    onError: () => {
      dispatch(
        addMessageAction(intl.formatMessage(messages.updateError), 'error'),
      );
    },
  });

  // -- Handler -------------------------------------------------------------------
  const handleToggle = () => {
    patchMergeKey(accountExternalId, mergeKey.external_id, {
      translatable: !mergeKey.translatable,
    });
  };

  // -- UI ------------------------------------------------------------------------
  return (
    <TableRow data-testid={`merge-key-row-${mergeKey.key}`}>
      <TableCell>
        <Typography variant="body2">{mergeKey.key}</Typography>
      </TableCell>
      <TableCell className={classes.toggleCell}>
        <Switch
          checked={mergeKey.translatable}
          color="primary"
          disabled={result.isLoading}
          onChange={handleToggle}
          inputProps={{
            'aria-label': intl.formatMessage(messages.toggleLabel),
          }}
        />
      </TableCell>
      <TableCell className={classes.actionsCell} align="right">
        <IconButton
          aria-label={intl.formatMessage(messages.deleteLabel)}
          onClick={() => onDeleteRequest(mergeKey)}
          size="small"
        >
          <Trashcan fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

// == Export ======================================================================
export default MergeKeyRow;
